import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Fetchexecutequery } from '../../../services/process/BRS1/brsService1';
import * as XLSX from "xlsx";
import Select from "react-select";

/* ───────────────── API ───────────────── */
async function runQuery(query) {
  const payload = { query };
  const [creds, url] = Fetchexecutequery();
  const res = await axios.post(url, payload, creds);
  return res.data;
}

/* ───────────────── GLOBAL FILTER BUILDER ───────────────── */
function buildWhereClause(filters = {}) {
  const { division, department, month } = filters;
  const conditions = [];
  if (division?.value)   conditions.push(`division_id = ${division.value}`);
  if (department?.value) conditions.push(`department_id = ${department.value}`);
  if (month)             conditions.push(`month = '${month}'`);
  return conditions.length ? `WHERE ${conditions.join(" AND ")}` : "";
}

/* ───────────────── VALIDATION LOGIC ───────────────── */
function validateRow(row) {
  const issues = [];
  const glCode     = row.gl_code ? String(row.gl_code) : null;
  const postKey    = row.POST_KEY ? Number(row.POST_KEY) : null;
  const specialInd = row.SPL_GL_IND;
  const costCent   = row.COST_CENT;
  const profCent   = row.PROF_CENT;
  const account    = row.description?.toLowerCase();

  if (account === "gl code" || !account) {
    if (!glCode) {
      issues.push({ rule: "NO_GL_CODE", msg: "GL code is missing" });
    } else if (glCode) {
      if (glCode.length !== 8)
        issues.push({ rule: "GL_LENGTH", msg: `GL code "${glCode}" must be exactly 8 digits` });
      const prefix = glCode[0];
      if (["6","7","4"].includes(prefix)) {
        if (prefix === "6") {
          if (!costCent || costCent.length === 0)
            issues.push({ rule: "EXPENSE_COST_CTR", msg: "Expense GL requires Cost Center" });
        } else {
          if (!profCent || profCent === "" || profCent === null)
            issues.push({ rule: prefix === "7" ? "INCOME_PROF_CTR" : "BS_PROF_CTR",
              msg: `${prefix === "7" ? "Income" : "Balance Sheet"} GL requires Profit Center` });
        }
      }
    }
  }
  if (!account || account === null) {
    issues.push({ rule: "NO_GL_CODE", msg: "Account is missing → treated as No GL Code" });
  }
  if (account === "vendor code") {
    if ([39,29].includes(postKey)) {
      if (!specialInd || specialInd.trim() === "" || specialInd === null)
        issues.push({ rule: "VENDOR_SPL_REQUIRED", msg: `Posting key ${postKey} must have Special Indicator` });
    }
    if ([31,21].includes(postKey)) {
      if (specialInd && specialInd.trim() !== "" && specialInd.length !== 0 && specialInd !== null)
        issues.push({ rule: "VENDOR_SPL_FORBIDDEN", msg: `Posting key ${postKey} must NOT have Special Indicator` });
    }
  }
  return issues;
}

/* ───────────────── RECO MAPPING ───────────────── */
const tagCols    = (cols, gl_desc) => cols.map(name => ({ name, gl_desc }));
const tagJvCols  = (cols)          => cols.map(name => ({ name }));

const RECO_MAP = [

  { key:"BASIC", label:"Basic Salary", section:"Gross Salary",
    match_by:"gl", gl_code:"64211000", gl_desc:"Salaries - Basic",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["BASIC SALARY"],"Salaries - Basic"),
    jv_cols:tagJvCols(["BASIC SALARY"]), sign:1 },

  { key:"FIXED_ALLOW", label:"Fixed Allowances", section:"Gross Salary",
    match_by:"gl", gl_code:"64241100", gl_desc:"Salaries - Fixed allowance",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["HOUSE RENT ALLOWANCE","SPECIAL ALLOWANCE","ADDITIONAL HRA","EDUCATION ALLOWANCE","TELEPHONE REIMBURSEMENT","OTHER ALLOWANCE","VEHICLE ALLOWANCE","REFFARAL","HYGIENE ALLOWANCE","BF & CF"],"Salaries - Fixed allowance"),
    jv_cols:tagJvCols(["HOUSE RENT ALLOWANCE","SPECIAL ALLOWANCE","AHRA","COMM.ALL","EDUCATION ALLOWANCE","RETENTION ALLLOWANCE","REFFARAL","VEHICLE ALLOWANCE","HYGIENE ALLOWANCE","B/F PAISE","C/F PAISE"]),
    sign:1 },

  { key:"BONUS", label:"Bonus", section:"Gross Salary",
    match_by:"gl", gl_code:"64231100", gl_desc:"Salaries - Fixed allowance",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["BONUS"],"Salaries - Bonus"),
    jv_cols:tagJvCols(["BONUS - FTC"]), sign:1 },

  { key:"INCENTIVE_PROV", label:"Incentive Provision", section:"Gross Salary",
    match_by:"gl", gl_code:"64231300", gl_desc:"Salaries - Incentives",
    jv_classification:["Performance Incentive Prov","Salary Entry"],
    reg_cols:tagCols(["FIXED INCENTIVE","MASTER INCENTIVE","RESTRUCTURED INCENTIVE"],"Salaries - Incentives"),
    jv_cols:tagJvCols(["FIXED INCENTIVE","MASTER INCENTIVE","RESTRUCTURED INCENTIVE"]),
    sign:1 },

  { key:"MARRIAGE", label:"Welfare - Gifts", section:"Gross Salary",
    match_by:"gl", gl_code:"62340000", gl_desc:"Welfare - Gifts",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["MARRIAGE GIFT"],"Welfare - Gifts"),
    jv_cols:tagJvCols(["WELFARE - GIFTS"]),
    sign:1 },

  { key:"PL_ENC", label:"PL Encashment", section:"Gross Salary",
    match_by:"gl", gl_code:"15340000", gl_desc:"Personnel - Leave Encashment Provision",
    jv_classification:["LE Provision"],
    reg_cols:tagCols(["PL ENCASHMENT"],"Personnel - Leave Encashment Provision"),
    jv_cols:tagJvCols(["PL LEAVE ENCASHMENT"]),
    sign:1 },

  { key:"INCENTIVES", label:"Incentives (Salary)", section:"Gross Salary",
    match_by:"gl", gl_code:"42830000", gl_desc:"Provision - Staff Incentive Payable",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["INCENTIVE"],"Provision - Staff Incentive Payable"),
    jv_cols:tagJvCols(["INCENTIVE PAID"]),
    sign:1 },

  { key:"LTA", label:"LTA", section:"Gross Salary",
    match_by:"gl", gl_code:"42820000", spl_gl:"Q", gl_desc:"LTA Payable - Employees",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["LTA TAXABLE"],"Vendor – LTA (SPL: Q)"),
    jv_cols:tagJvCols(["LTA TAXABLE"]),
    sign:1 },

  { key:"TDS", label:"TDS", section:"Deduction",
    match_by:"gl", gl_code:"44573500", gl_desc:"TDS - Salary",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["INCOME TAX DEDUCTION","ADDL. INCOME TAX DEDUCTION"],"TDS - Salary"),
    jv_cols:tagJvCols(["INCOME TAX","ADDITIONAL INCOME TAX RECOVERY"]),
    sign:-1 },

  { key:"PF", label:"PF", section:"Deduction",
    match_by:"gl", gl_code:"43710001", gl_desc:"PF Payable - Employee",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["PROVIDENT FUND DEDUCTION","VOLUNTARY P F"],"PF Payable - Employee"),
    jv_cols:tagJvCols(["EMPLOYEE PROVIDENT FUND","FAMILY PENSION FUND","VOLUNTARY PROVIDENT FUND"]),
    sign:-1 },

  { key:"PT", label:"Profession Tax", section:"Deduction",
    match_by:"gl", gl_code:"40111000", spl_gl:"S", gl_desc:"Profession Tax",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["PROFESSION TAX"],"Profession Tax"),
    jv_cols:tagJvCols(["PROFESSION TAX AP","PROFESSION TAX GJ","PROFESSION TAX KA","PROFESSION TAX MH","PROFESSION TAX TN","PROFESSION TAX WB"]),
    sign:-1 },

  { key:"CANTEEN", label:"Canteen Recovery", section:"Deduction",
    match_by:"gl", gl_code:"40111000", gl_desc:"Trade Creditor - THP Domestic",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["CANTEEN DEDUCTION"],"Vendor – Canteen"),
    jv_cols:tagJvCols(["CANTEEN RECOVERY"]),
    sign:-1 },

  { key:"ESIC", label:"ESIC", section:"Deduction",
    match_by:"gl", gl_code:"43100000", gl_desc:"ESIC Payable - Employee",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["ESIC DEDUCTION"],"ESIC Payable - Employee"),
    jv_cols:tagJvCols(["ESIC - EMPLOYEE"]),
    sign:-1 },

  { key:"MEDICLAIM", label:"Mediclaim", section:"Deduction",
    match_by:"gl", gl_code:"64610000", gl_desc:"Salaries - Health care contribution",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["MEDICLAIM PREMIUM RECOVERY"],"Salaries - Health care contribution"),
    jv_cols:tagJvCols(["MEDICLAIM PREMIUM RECOVERY"]),
    sign:-1 },

  { key:"OTHER_DED", label:"Other Deduction", section:"Deduction",
    match_by:"gl", gl_code:"42861000", gl_desc:"Payroll Recovery",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["OTHER DEDUCTION"],"Payroll Recovery"),
    jv_cols:tagJvCols(["OTHER DEDUCTION"]),
    sign:-1 },

  { key:"SALARY_ADV", label:"Salary Advance Recovery", section:"Deduction",
    match_by:"gl", gl_code:"42210000", spl_gl:"Z", gl_desc:"Salary Advance",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["SALARY ADVANCE"],"Salary Advance"),
    jv_cols:tagJvCols(["SALARY ADV RECOVERY"]),
    sign:-1 },

  { key:"BENEVOLENT", label:"Benevolent / LIC / Society", section:"Deduction",
    match_by:"gl", gl_code:"40111000", spl_gl:"N", gl_desc:"Trade Creditor - THP Domestic",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["BENEVOLENT FUND LOAN","BENEVOLENT FUND CONTRIBUTION","SALARY SAVING SCHEME","SOCIETY SHARES"],"Trade Creditor"),
    jv_cols:tagJvCols(["BENEVOLANT FUND CONTR.","BENEVOLANT FUND LOAN","LIC - BANGALORE","SOCIETY"]),
    sign:-1 },

  { key:"HOUSING_LOAN_INT", label:"Loan Interest", section:"Deduction",
    match_by:"gl", gl_code:"76130000", gl_desc:"Interest Incomes - Employee Loan",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["HOUSING LOAN INTEREST","PERSONAL LOAN INTEREST"],"Interest Incomes - Employee Loan"),
    jv_cols:tagJvCols(["HOUSING LOAN INTEREST","LOAN INTEREST"]),
    sign:-1 },

  { key:"LOAN_PRINCIPAL", label:"Loan Principal Recovery", section:"Deduction",
    match_by:"gl", gl_code:"42210000", spl_gl:"D", gl_desc:"Personal Loan Principal",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["HOUSING LOAN PRINCIPAL","PERSONAL LOAN PRINCIPAL"],"Personal Loan Principal"),
    jv_cols:tagJvCols(["HOUSING LOAN RECOVERY","LOAN 10 %"]),
    sign:-1 },

  { key:"NET_PAY", label:"Net Pay", section:"NET_PAY",
    match_by:"gl", gl_code:"42100000", gl_desc:"Salaries Payable",
    jv_classification:["Salary Entry"],
    reg_cols:tagCols(["NET PAY"],"Salaries Payable"),
    jv_cols:tagJvCols(["NET_PAY"]),
    sign:-1 },
];

const SECTIONS = {
  "Gross Salary": { color: "#f97316", bg: "#fff8f5", border: "#fed7aa" },
  "Deduction":    { color: "#1e3a5f", bg: "#f0f4fa", border: "#c7d9f0" },
  "NET_PAY":      { color: "#0f172a", bg: "#f8fafc", border: "#e2e8f0" },
};

/* ───────────────── UNIFIED PALETTE ───────────────── */
// Derived from app's navy sidebar + orange top nav
const NAVY        = "#0f1623";      // sidebar background
const NAVY_MID    = "#1a2744";      // card headers / dark rows
const NAVY_LIGHT  = "#1e3a5f";      // secondary navy
const ORANGE = "#f97316";   // brand  // amber (different!)      // primary accent (top nav)
const ORANGE_DARK = "#ea580c";      // darker orange for hover/pressed
const ORANGE_SOFT = "#fff4ed";      // orange tint bg

const CARD_BG     = "#ffffff";
const PAGE_BG     = "#f4f7fc";
const BORDER      = "#e2e8f0";
const BORDER_DARK = "#cbd5e1";

const TEXT_MAIN   = "#0f172a";
const TEXT_SEC    = "#475569";
const TEXT_MUTED  = "#94a3b8";

// Status colours — kept minimal: green for OK, orange for warn, navy-red for error
const SUCCESS     = "#16a34a";
const WARN        = "#f97316";   // reuse orange so it harmonises
const DANGER      = "#be123c";

// Data series — 4 restrained hues that sit well on white and navy
const SERIES = {
  A: "#1e3a5f",   // navy — debit / primary data
  B: "#f97316",   // orange — credit / secondary data
  C: "#16a34a",   // green — matched / income
  D: "#be123c",   // crimson — errors / expense
};

/* ───────────────── SELECT STYLES ───────────────── */
const compactSelectStyles = {
  container:          (b) => ({ ...b, width: "100%" }),
  control:            (b) => ({ ...b, minHeight: 32, height: 32, fontSize: 13, backgroundColor: "#f1f5f9", borderColor: BORDER_DARK, borderRadius: 6, boxShadow: "none", "&:hover": { borderColor: ORANGE } }),
  valueContainer:     (b) => ({ ...b, height: 32, padding: "0 10px" }),
  input:              (b) => ({ ...b, margin: 0, padding: 0, color: TEXT_MAIN }),
  singleValue:        (b) => ({ ...b, color: TEXT_MAIN, fontWeight: 600, fontSize: 13 }),
  placeholder:        (b) => ({ ...b, color: TEXT_MUTED, fontSize: 13 }),
  menu:               (b) => ({ ...b, backgroundColor: "#fff", borderRadius: 6, border: `1px solid ${BORDER_DARK}`, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }),
  option:             (b, s) => ({ ...b, backgroundColor: s.isSelected ? NAVY_LIGHT : s.isFocused ? ORANGE_SOFT : "#fff", color: s.isSelected ? "#fff" : TEXT_MAIN, fontSize: 13, cursor: "pointer" }),
  indicatorsContainer:(b) => ({ ...b, height: 32 }),
  dropdownIndicator:  (b) => ({ ...b, padding: 6, color: TEXT_MUTED }),
  clearIndicator:     (b) => ({ ...b, padding: 6, color: TEXT_MUTED }),
  indicatorSeparator: ()  => ({ display: "none" }),
};

/* ───────────────── RULE CONFIG ───────────────── */
// All use the same 2-colour system: orange (warn) or crimson (error)
const RULE_COLOR = {
  GL_LENGTH:           DANGER,
  EXPENSE_COST_CTR:    WARN,
  NO_GL_CODE:          TEXT_SEC,
  INCOME_PROF_CTR:     NAVY_LIGHT,
  BS_PROF_CTR:         NAVY_LIGHT,
  VENDOR_SPL_REQUIRED: DANGER,
  VENDOR_SPL_FORBIDDEN:WARN,
};
const ruleLabel = {
  GL_LENGTH:           "GL Length",
  EXPENSE_COST_CTR:    "Missing Cost Ctr",
  NO_GL_CODE:          "No GL Code",
  INCOME_PROF_CTR:     "Missing Profit Ctr (Income)",
  BS_PROF_CTR:         "Missing Profit Ctr (BS)",
  VENDOR_SPL_REQUIRED: "SPL Required",
  VENDOR_SPL_FORBIDDEN:"SPL Forbidden",
};

// Posting key: debit = navy, credit = orange
const PK_COLOR_MAP = {
  "21": NAVY_LIGHT,
  "29": NAVY_LIGHT,
  "40": NAVY,
  "31": ORANGE,
  "39": ORANGE_DARK,
  "50": WARN,
};

const JV_CLASS_COLOR_MAP = {
  "Salary Entry":             SERIES.A,
  "Performance Incentive Prov": ORANGE,
  "LE Provision":             WARN,
  "Salary Entry (Adj)":       SERIES.C,
  "Bonus":                    NAVY_LIGHT,
  "Incentive":                SERIES.B,
  "Provision":                WARN,
  "Recovery":                 SERIES.A,
  "Deduction":                DANGER,
  "UNCLASSIFIED":             TEXT_MUTED,
  "DEFAULT":                  BORDER_DARK,
};

const SPL_COLOR_MAP = {
  NO_SPL:  TEXT_MUTED,
  Q:       ORANGE,
  S:       SUCCESS,
  Z:       DANGER,
  N:       NAVY_LIGHT,
  D:       WARN,
  DEFAULT: BORDER_DARK,
};

/* ───────────────── FORMATTERS ───────────────── */
const fmt       = n => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
const fmtAmt    = n => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n))}`;
const fmtSigned = n => (n < 0 ? "−" : "") + `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n))}`;

/* ═══════════════════════════════════════════════════════════════════
   SHARED PRIMITIVES
═══════════════════════════════════════════════════════════════════ */

// Standard finance fonts - Arial/Helvetica for readability
const FONT = "Arial, Helvetica, sans-serif";
const FONT_MONO = "Courier New, monospace";

// Pill badge — uses navy/orange/green/crimson only
function Badge({ color, children, small }) {
  return (
    <span style={{
      background: `${color}15`,
      color,
      border: `1px solid ${color}40`,
      borderRadius: 4,
      padding: small ? "1px 6px" : "2px 8px",
      fontSize: small ? 10 : 11,
      fontWeight: 700,
      letterSpacing: "0.2px",
      whiteSpace: "nowrap",
      fontFamily: FONT,
    }}>{children}</span>
  );
}

function ModeToggle({ value, onChange }) {
  return (
    <div style={{ display: "flex", background: "#e8eef7", borderRadius: 5, padding: 2, gap: 2 }}>
      {["Count", "Value"].map(opt => (
        <button key={opt} onClick={() => onChange(opt)} style={{
          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
          border: "none", cursor: "pointer", transition: "all 0.15s",
          background: value === opt ? NAVY : "transparent",
          color: value === opt ? "#fff" : TEXT_SEC,
          fontFamily: FONT,
        }}>{opt}</button>
      ))}
    </div>
  );
}

function PkBadge({ pk }) {
  const isDr = ["21","29","40"].includes(String(pk));
  const color = PK_COLOR_MAP[String(pk)] || TEXT_MUTED;
  return (
    <span style={{
      background: `${color}15`, color, border: `1px solid ${color}40`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700,
      fontFamily: FONT,
    }}>
      {pk} <span style={{ opacity: 0.7, fontSize: 9 }}>{isDr ? "DR" : "CR"}</span>
    </span>
  );
}

/* ── Compact horizontal bar row ─────────────────────────────────── */
function BarRow({ label, rawCount, rawValue, pct, color, mode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 16,fontWeight: 700, color: TEXT_SEC, fontFamily: FONT }}>{label}</span>
        </div>
        <span style={{ fontSize: 16,fontWeight: 700, fontWeight: 700, color, fontFamily: FONT }}>
          {mode === "Value" ? rawValue : rawCount}
        </span>
      </div>
      <div style={{ height: 4, background: "#e8eef7", borderRadius: 3 }}>
        <div style={{ height: "100%", borderRadius: 3, background: color, width: `${pct}%`, transition: "width 0.35s" }} />
      </div>
    </div>
  );
}

/* ── DonutChart ─────────────────────────────── */
function DonutChart({ data, onLegendClick, activeFilter }) {
  const [hovered, setHovered] = useState(null);
  const filtered = data.filter(d => d.value > 0);
  const total    = filtered.reduce((s, d) => s + d.value, 0);
  const CX = 90, CY = 90, R = 78;
  let cumulative = 0;
  const slices = filtered.map((d, i) => {
    const pct = d.value / total, s = cumulative * 2 * Math.PI - Math.PI / 2,
          e = (cumulative + pct) * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const mid = s + (e - s) / 2, lr = R * 0.60, pr = 7;
    return { ...d, pct, x1: CX + R * Math.cos(s), y1: CY + R * Math.sin(s),
      x2: CX + R * Math.cos(e), y2: CY + R * Math.sin(e), largeArc: pct > 0.5 ? 1 : 0,
      midAngle: mid, labelX: CX + lr * Math.cos(mid), labelY: CY + lr * Math.sin(mid),
      pullX: pr * Math.cos(mid), pullY: pr * Math.sin(mid) };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <div style={{ flexShrink: 0 }}>
        <svg width={120} height={140} viewBox="-12 -12 204 204" style={{ overflow: "visible" }}>
          {total === 0 ? <circle cx={CX} cy={CY} r={R} fill="#e8eef7" /> :
            slices.map((seg, i) => {
              const isHov = hovered === i, tx = isHov ? seg.pullX : 0, ty = isHov ? seg.pullY : 0;
              if (filtered.length === 1) return <circle key={i} cx={CX + tx} cy={CY + ty} r={R} fill={seg.color}
                stroke="#fff" strokeWidth={2} style={{ cursor: "pointer" }}
                onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} />;
              const path = `M ${CX+tx} ${CY+ty} L ${seg.x1+tx} ${seg.y1+ty} A ${R} ${R} 0 ${seg.largeArc} 1 ${seg.x2+tx} ${seg.y2+ty} Z`;
              return (
                <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: "pointer" }}>
                  <path d={path} fill={seg.color} stroke="#fff" strokeWidth={isHov ? 2.5 : 1.5}
                    style={{ filter: isHov ? `drop-shadow(0 3px 10px ${seg.color}88)` : "none" }} />
                  {seg.pct > 0.06 && <text x={seg.labelX + tx} y={seg.labelY + ty + 4} textAnchor="middle"
                    fill="#fff" fontSize={isHov ? 12 : 10} fontWeight={700} fontFamily={FONT}
                    style={{ pointerEvents: "none" }}>{(seg.pct * 100).toFixed(0)}%</text>}
                </g>
              );
            })}
        </svg>
      </div>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, paddingLeft: 16 }}>
        {data.map((r, i) => {
          const sliceIdx = slices.findIndex(s => s.color === r.color && s.label === r.label);
          const isHov = hovered === sliceIdx && sliceIdx !== -1, isActive = r.rule === activeFilter, hasVal = r.value > 0;
          return (
            <div key={i} onClick={() => hasVal && onLegendClick && onLegendClick(r.rule)}
              onMouseEnter={() => sliceIdx !== -1 && setHovered(sliceIdx)} onMouseLeave={() => setHovered(null)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "5px 10px", borderRadius: 6, cursor: hasVal ? "pointer" : "default",
                background: isActive ? `${r.color}15` : isHov ? `${r.color}0d` : "transparent",
                border: `1.5px solid ${isActive ? `${r.color}60` : isHov ? `${r.color}30` : "transparent"}`,
                transition: "all 0.15s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: r.color, opacity: hasVal ? 1 : 0.25 }} />
                <span style={{ fontSize: 16,fontWeight: 700, fontWeight: isActive || isHov ? 700 : 500, fontFamily: FONT,
                  color: isActive ? r.color : isHov ? r.color : hasVal ? TEXT_MAIN : TEXT_MUTED }}>{r.label}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, flexShrink: 0, color: hasVal ? r.color : TEXT_MUTED, marginLeft: 10,
                fontFamily: FONT }}>{r.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── MiniDonut ─────────────────────────────── */
function MiniDonut({ data, onLegendClick, activeFilter }) {
  const [hov, setHov] = useState(null);
  const filtered = data.filter(d => d.value > 0);
  const total    = filtered.reduce((s, d) => s + d.value, 0) || 1;
  const CX = 55, CY = 55, R = 46;
  let cum = 0;
  const slices = filtered.map((d, i) => {
    const pct = d.value / total, s = cum * 2 * Math.PI - Math.PI / 2, e = (cum + pct) * 2 * Math.PI - Math.PI / 2;
    cum += pct;
    const mid = s + (e - s) / 2;
    return { ...d, pct, x1: CX + R * Math.cos(s), y1: CY + R * Math.sin(s),
      x2: CX + R * Math.cos(e), y2: CY + R * Math.sin(e), largeArc: pct > 0.5 ? 1 : 0, mid,
      px: 5 * Math.cos(mid), py: 5 * Math.sin(mid) };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width={110} height={110} viewBox="-8 -8 126 126" style={{ overflow: "visible", flexShrink: 0 }}>
        {total === 0 ? <circle cx={CX} cy={CY} r={R} fill="#e8eef7" /> :
          slices.map((seg, i) => {
            const isH = hov === i, tx = isH ? seg.px : 0, ty = isH ? seg.py : 0;
            if (filtered.length === 1) return <circle key={i} cx={CX + tx} cy={CY + ty} r={R} fill={seg.color}
              stroke="#fff" strokeWidth={2} style={{ cursor: "pointer" }}
              onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} />;
            const path = `M ${CX+tx} ${CY+ty} L ${seg.x1+tx} ${seg.y1+ty} A ${R} ${R} 0 ${seg.largeArc} 1 ${seg.x2+tx} ${seg.y2+ty} Z`;
            return <g key={i} onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} style={{ cursor: "pointer" }}>
              <path d={path} fill={seg.color} stroke="#fff" strokeWidth={isH ? 2 : 1.5}
                style={{ filter: isH ? `drop-shadow(0 3px 8px ${seg.color}88)` : "none" }} />
              {seg.pct > 0.08 && <text x={CX + (R * 0.6) * Math.cos(seg.mid) + tx} y={CY + (R * 0.6) * Math.sin(seg.mid) + ty + 3}
                textAnchor="middle" fill="#fff" fontSize={9} fontWeight={700} fontFamily={FONT}>
                {(seg.pct * 100).toFixed(0)}%</text>}
            </g>;
          })}
      </svg>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 6px", flex: 1 }}>
        {data.map((r, i) => {
          const si = slices.findIndex(s => s.color === r.color && s.label === r.label);
          const isA = r.rule === activeFilter, isH = hov === si && si !== -1, hasV = r.value > 0;
          return (
            <div key={i} onClick={() => hasV && onLegendClick && onLegendClick(r.rule)}
              onMouseEnter={() => si !== -1 && setHov(si)} onMouseLeave={() => setHov(null)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "3px 7px", borderRadius: 4, cursor: hasV ? "pointer" : "default",
                background: isA ? `${r.color}15` : isH ? `${r.color}0d` : "transparent",
                border: `1px solid ${isA ? `${r.color}60` : isH ? `${r.color}30` : "transparent"}`,
                transition: "all 0.12s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: r.color, opacity: hasV ? 1 : 0.3, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: isA || isH ? 700 : 500, fontFamily: FONT,
                  color: isA ? r.color : isH ? r.color : hasV ? TEXT_MAIN : TEXT_MUTED, lineHeight: 1.2 }}>{r.label}</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: hasV ? r.color : TEXT_MUTED, marginLeft: 4,
                fontFamily: FONT }}>{r.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   RECO TAB
═══════════════════════════════════════════════════════════════════ */
function RecoTab({ division, department, month }) {
  const [jvRows,  setJvRows]  = useState([]);
  const [regRow,  setRegRow]  = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({"Gross Salary": true, "Deduction": true, "NET_PAY": true});
  const tdStyle = {
  padding: "10px 14px",
  borderBottom: `1px solid ${BORDER}`,
  fontWeight: 600,
  whiteSpace: "nowrap"
};

const tdWrap = {
  ...tdStyle,
  whiteSpace: "normal",
  wordBreak: "break-word"
};

const tdNum = (color) => ({
  ...tdStyle,
  textAlign: "right",
  fontWeight: 800,
  color
});

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const whereClause = buildWhereClause({ division, department, month });
      const [jvData, regData] = await Promise.all([
        runQuery(`SELECT * FROM jv_recon.jv ${whereClause};`),
        runQuery(`SELECT * FROM jv_recon.salary_register ${whereClause} LIMIT 1;`),
      ]);
      setJvRows(Array.isArray(jvData) ? jvData : jvData?.rows || []);
      const reg = Array.isArray(regData) ? regData : regData?.rows || [];
      setRegRow(reg[0] || {});
    } catch(e) { setError(e.message || "Failed to fetch"); }
    finally    { setLoading(false); }
  }, [division, department, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSection = sec => setCollapsedSections(prev => ({ ...prev, [sec]: !prev[sec] }));
  console.log(collapsedSections);
  const salaryEntryRows = jvRows.filter(r => String(r.type || r.TYPE || "").trim() === "Salary Entry");

  const byLineItem = {};
  salaryEntryRows.forEach(r => {
    const lineItem = String(r.line_item || r.LINE_ITEM || "").trim();
    if (!lineItem) return;
    const pk = Number(r.post_key || r.POST_KEY || 0), amt = Number(r.amount || r.AMOUNT || 0);
    const isDr = [40,29,21].includes(pk), isCr = [50,39,31].includes(pk);
    if (!byLineItem[lineItem]) byLineItem[lineItem] = { debit: 0, credit: 0 };
    if (isDr) byLineItem[lineItem].debit  += amt;
    if (isCr) byLineItem[lineItem].credit += amt;
  });

  function getJvNet(line) {
    let debit = 0, credit = 0;
    (line.jv_cols || []).forEach(col => { const agg = byLineItem[col.name]; if (agg) { debit += agg.debit; credit += agg.credit; } });
    return (debit - credit) * line.sign;
  }
  function getRegVal(col) {
    const name = typeof col === "object" ? col.name : col;
    if (regRow[name] !== undefined) return Number(regRow[name]) || 0;
    const key = Object.keys(regRow).find(k => k.toLowerCase() === name.toLowerCase());
    return key ? Number(regRow[key]) || 0 : 0;
  }
  function getRegNet(line) { return line.reg_cols.reduce((s, col) => s + Math.abs(getRegVal(col)), 0); }

  const recoLines = RECO_MAP.map(line => {
    const jvNet = getJvNet(line), regNet = getRegNet(line), diff = jvNet - regNet;
    return { ...line, jvNet, regNet, diff, matched: Math.abs(diff) < 1 };
  });

  const sectionTotals = {};
  Object.keys(SECTIONS).forEach(sec => {
    const lines = recoLines.filter(r => r.section === sec);
    sectionTotals[sec] = {
      jvNet:  lines.reduce((s, r) => s + Math.abs(r.jvNet), 0),
      regNet: lines.reduce((s, r) => s + Math.abs(r.regNet), 0),
      matched: lines.filter(r => r.matched).length, total: lines.length,
    };
    sectionTotals[sec].diff = Math.abs(sectionTotals[sec].jvNet - sectionTotals[sec].regNet);
    sectionTotals[sec].isBalanced = sectionTotals[sec].diff < 1;
  });

  const totalLines   = recoLines.length;
  const matchedLines = recoLines.filter(r => r.matched).length;
  const matchPct     = totalLines ? Math.round(matchedLines / totalLines * 100) : 0;
  const totalVariance= recoLines.reduce((s, r) => s + Math.abs(r.diff), 0);
  const isFullyMatch = totalVariance < 1;
  const matchColor   = matchPct === 100 ? SUCCESS : matchPct >= 90 ? WARN : DANGER;

  let totalDebit = 0, totalCredit = 0;
  salaryEntryRows.forEach(r => {
    const pk = Number(r.post_key || r.POST_KEY || 0), amt = Number(r.amount || r.AMOUNT || 0);
    if ([40,29,21].includes(pk)) totalDebit += amt;
    else if ([50,39,31].includes(pk)) totalCredit += amt;
  });
  const jvBalance = totalDebit - totalCredit, jvBalanced = Math.abs(jvBalance) < 1;

  const exportReco = () => {
    const recoSheet = XLSX.utils.json_to_sheet(recoLines.map(r => ({
      Section: r.section, Line_Item: r.label, GL_Desc: r.gl_desc,
      JV_Line_Items: r.jv_cols.map(c => c.name).join(", "),
      Register_Columns: r.reg_cols.map(c => c.name).join(", "),
      JV_Net: r.jvNet, Register_Net: r.regNet, Difference: r.diff,
      Status: r.matched ? "MATCHED" : "MISMATCH",
    })));
    const jvSheet  = XLSX.utils.json_to_sheet(salaryEntryRows);
    const regSheet = XLSX.utils.json_to_sheet([regRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recoSheet, "Reco");
    XLSX.utils.book_append_sheet(wb, jvSheet, "JV_Data");
    XLSX.utils.book_append_sheet(wb, regSheet, "Register");
    XLSX.writeFile(wb, `JV_Reco_Full_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: 80, color: TEXT_SEC, fontFamily: FONT }}>
      <div style={{ fontSize: 28, marginBottom: 10, color: ORANGE }}>◌</div>
      <div style={{ fontWeight: 600, fontSize: 15 }}>Loading Reconciliation…</div>
    </div>
  );
  if (error) return (
    <div style={{ background: "#fff0f0", border: `1.5px solid ${DANGER}`, borderRadius: 8, padding: 14, color: DANGER, fontSize: 13, fontFamily: FONT }}>⚠ {error}</div>
  );

  const TABLE_HEADERS = [
    { label: "Line Item", right: false }, { label: "GL Code", right: false },{ label: "GL Description", right: false },
    { label: "JV Net", right: true }, { label: "Register", right: true },
    { label: "Variance", right: true }, { label: "Status", right: false },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: FONT }}>

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10 }}>
        {[
          { label: "Reco Lines",     value: fmt(totalLines),              color: NAVY_LIGHT, icon: "≡" },
          { label: "Matched",        value: fmt(matchedLines),            color: SUCCESS,    icon: "✓" },
          { label: "Mismatched",     value: fmt(totalLines - matchedLines), color: DANGER,   icon: "✗" },
          { label: "Match %",        value: `${matchPct}%`,              color: matchColor, icon: "%" },
          { label: "Total Variance", value: fmtAmt(totalVariance),       color: isFullyMatch ? SUCCESS : DANGER, icon: "Δ" },
          { label: "JV Balanced",    value: jvBalanced ? "Yes" : "No",   color: jvBalanced ? SUCCESS : DANGER,   icon: "⚖" },
        ].map((k, i) => (
          <div key={i} style={{ background: CARD_BG, borderRadius: 8, padding: "12px 14px", border: `1.5px solid ${BORDER}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.6px" }}>{k.label}</div>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: `${k.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,fontWeight: 700, fontWeight: 800, color: k.color }}>{k.icon}</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color, marginTop: 8, letterSpacing: "-0.5px" }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Summary row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>

        {/* JV Dr/Cr — navy card */}
        <div style={{ background: NAVY, borderRadius: 8, padding: "14px 16px", boxShadow: "0 4px 20px rgba(15,22,35,0.3)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6b8ab0", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 10 }}>JV Debit / Credit</div>
          {[
            { label: "Total Debit",  value: fmtAmt(totalDebit),           color: "#60a5fa" },
            { label: "Total Credit", value: fmtAmt(totalCredit),          color: ORANGE },
            { label: "Net Balance",  value: fmtSigned(jvBalance),         color: jvBalanced ? "#4ade80" : "#f87171" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "7px 0", borderBottom: i < 2 ? "1px solid #1e3460" : "none" }}>
              <span style={{ fontSize: 13, color: "#94b4d4" }}>{item.label}</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: item.color, letterSpacing: "-0.5px" }}>{item.value}</span>
            </div>
          ))}
        </div>

        {/* Section summary */}
        <div style={{ background: CARD_BG, borderRadius: 8, padding: "14px 16px", border: `1.5px solid ${BORDER}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 10 }}>Section Summary</div>
          {Object.entries(sectionTotals).map(([sec, s], i) => {
            const c = SECTIONS[sec].color;
            return (
              <div key={sec} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                  <span style={{ fontSize: 13, color: TEXT_SEC }}>{sec}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: c }}>{fmtAmt(s.jvNet)}</span>
                  <Badge color={s.matched === s.total ? SUCCESS : DANGER} small>{s.matched}/{s.total}</Badge>
                </div>
              </div>
            );
          })}
        </div>

        {/* Match gauge */}
        <div style={{ background: CARD_BG, borderRadius: 8, padding: "14px 16px", border: `1.5px solid ${BORDER}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <svg width={120} height={120} viewBox="0 0 110 110">
            <circle cx={55} cy={55} r={42} fill="none" stroke="#e8eef7" strokeWidth={12} />
            <circle cx={55} cy={55} r={42} fill="none" stroke={matchColor} strokeWidth={12} strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 42 * matchPct / 100} ${2 * Math.PI * 42 * (1 - matchPct / 100)}`}
              transform="rotate(-90 55 55)" />
            <text x={55} y={51} textAnchor="middle" fontSize={20} fontWeight={800} fill={matchColor} fontFamily={FONT}>{matchPct}%</text>
            <text x={55} y={65} textAnchor="middle" fontSize={10} fill={TEXT_MUTED} fontFamily={FONT}>matched</text>
          </svg>
          <div style={{ display: "flex", gap: 20, marginTop: 6 }}>
            {[{ label: "Match", value: matchedLines, color: SUCCESS }, { label: "Miss", value: totalLines - matchedLines, color: DANGER }].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reco accordion */}
      <div style={{ background: CARD_BG, borderRadius: 8, border: `1.5px solid ${BORDER}`, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1.5px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: TEXT_MAIN }}>Line-wise Reconciliation</div>
            <div style={{ fontSize: 16,fontWeight: 700, color: TEXT_MUTED, marginTop: 2 }}>Salary Entry rows · matched by JV <code>line_item</code> → salary register · click sections to expand</div>
          </div>
          <button onClick={exportReco} style={{ background: NAVY, border: "none", color: "#fff", borderRadius: 6, padding: "7px 16px", cursor: "pointer", fontSize: 16,fontWeight: 700, fontWeight: 600, letterSpacing: "0.2px" }}>
            ⬇ Export Reco
          </button>
        </div>

        {Object.keys(SECTIONS).map(sectionKey => {
          const sec = SECTIONS[sectionKey], secLines = recoLines.filter(r => r.section === sectionKey);
          const secTotals = sectionTotals[sectionKey], isCollapsed = !!collapsedSections[sectionKey];
          const varC = secTotals.isBalanced ? SUCCESS : DANGER;
          return (
            <div key={sectionKey} style={{ borderBottom: `2px solid ${sec.border}` }}>
              <div onClick={() => toggleSection(sectionKey)}
                style={{ display: "flex", alignItems: "stretch", cursor: "pointer", borderLeft: `4px solid ${sec.color}`, background: sec.bg, userSelect: "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", minWidth: 220, borderRight: `1px solid ${sec.border}`, flexShrink: 0 }}>
                  <span style={{ fontSize: 14, color: sec.color, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▾</span>
                  <div>
                    <div style={{ fontSize: 16,fontWeight: 700, fontWeight: 800, color: sec.color, textTransform: "uppercase", letterSpacing: "1px" }}>{sectionKey}</div>
                    <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}>{secLines.length} lines</div>
                  </div>
                </div>
                <div style={{ display: "flex", flex: 1, alignItems: "stretch" }}>
                  {[
                    { label: "JV Net",   value: fmtAmt(secTotals.jvNet),  color: SERIES.A },
                    { label: "Register", value: fmtAmt(secTotals.regNet), color: NAVY_LIGHT },
                    { label: "Variance", value: secTotals.isBalanced ? "NIL" : fmtAmt(secTotals.diff), color: varC },
                    { label: "Matched",  value: `${secTotals.matched} / ${secTotals.total}`, color: secTotals.matched === secTotals.total ? SUCCESS : DANGER },
                  ].map((tile, ti) => (
                    <div key={ti} style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1, padding: "10px", borderRight: `1px solid ${sec.border}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{tile.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: tile.color }}>{tile.value}</div>
                    </div>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "10px 18px" }}>
                    <Badge color={secTotals.isBalanced ? SUCCESS : DANGER}>{secTotals.isBalanced ? "✓ BALANCED" : "✗ VARIANCE"}</Badge>
                  </div>
                </div>
              </div>

              {!isCollapsed && (
                <div style={{ overflowX: "auto", borderLeft: `4px solid ${sec.color}33` }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT }}>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        {TABLE_HEADERS.map(h => (
                          <th key={h.label} style={{ padding: "9px 14px", textAlign: h.right ? "right" : "left", color: TEXT_MUTED, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap", borderBottom: `1.5px solid ${BORDER}` }}>{h.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {secLines.map((r, i) => {
                        const isExp = expanded === r.key, statusC = r.matched ? SUCCESS : DANGER;
                        const rowVarC = Math.abs(r.diff) < 1 ? SUCCESS : Math.abs(r.diff) < 10000 ? WARN : DANGER;
                        const jvColBreakdown = (r.jv_cols || []).map(col => {
                          const agg = byLineItem[col.name] || { debit: 0, credit: 0 };
                          return { name: col.name, debit: agg.debit, credit: agg.credit, net: (agg.debit - agg.credit) * r.sign };
                        });
                        return [
                          <tr
  key={r.key}
  onClick={() => setExpanded(isExp ? null : r.key)}
  style={{
    background: isExp ? ORANGE_SOFT : i % 2 === 0 ? "#fff" : "#fafbfd",
    cursor: "pointer"
  }}
>
  {/* Line Item */}
  <td style={tdStyle}>{r.label}</td>

  {/* GL Code */}
  <td style={tdStyle}>
    <span style={{ fontFamily: FONT_MONO, fontWeight: 700 }}>
      {r.gl_code || r.gl_sap || r.spl_gl || "—"}
    </span>
  </td>

  {/* GL Description */}
  <td style={tdWrap}>{r.gl_desc}</td>

  {/* JV Net */}
  <td style={tdNum(SERIES.A)}>{fmtAmt(r.jvNet)}</td>

  {/* Register */}
  <td style={tdNum(NAVY_LIGHT)}>{fmtAmt(r.regNet)}</td>

  {/* Variance */}
  <td style={tdNum(rowVarC)}>
    {Math.abs(r.diff) < 1 ? "—" : fmtAmt(r.diff)}
  </td>

  {/* Status */}
  <td style={{ ...tdStyle, textAlign: "center" }}>
    <Badge color={statusC}>
      {r.matched ? "✓ MATCHED" : "✗ MISMATCH"}
    </Badge>
  </td>
</tr>,

                          isExp && (
                            <tr key={r.key + "_exp"}>
                              <td colSpan={8} style={{ background: ORANGE_SOFT, borderBottom: `1px solid ${BORDER}`, borderLeft: `4px solid ${sec.color}`, padding: "14px 20px 16px 22px" }}>
                                <div style={{ display: "grid",
gridTemplateColumns: "1fr 1fr 260px",
gap: 20 }}>
                                  <div style={{ flex: "1 1 0",minWidth:0 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: NAVY_LIGHT, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>JV Line Items</div>
                                    <table style={{
  width: "100%",
  tableLayout: "fixed",   // 🔥 critical
  borderCollapse: "collapse"
}}>
                                      <thead>
                                        <tr style={{ background: `${NAVY_LIGHT}15` }}>
                                          <th style={{ width: "40%" }}>Line Item</th>
<th style={{ width: "20%", textAlign: "right" }}>Debit</th>
<th style={{ width: "20%", textAlign: "right" }}>Credit</th>
<th style={{ width: "20%", textAlign: "right" }}>Net</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {jvColBreakdown.map((col, j) => (
                                          <tr key={j} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                            <td style={{ padding: "5px 10px", color: TEXT_MAIN, fontWeight: 600, fontSize: 16,fontWeight: 700 }}>{col.name}</td>
                                            <td style={{ padding: "5px 10px", textAlign: "right", color: SERIES.A, fontWeight: 600, fontSize: 16,fontWeight: 700 }}>{col.debit ? fmtAmt(col.debit) : "—"}</td>
                                            <td style={{ padding: "5px 10px", textAlign: "right", color: ORANGE, fontWeight: 600, fontSize: 16,fontWeight: 700 }}>{col.credit ? fmtAmt(col.credit) : "—"}</td>
                                            <td style={{ padding: "5px 10px", textAlign: "right", color: col.net >= 0 ? SUCCESS : DANGER, fontWeight: 700, fontSize: 16,fontWeight: 700 }}>{fmtAmt(col.net)}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ background: `${NAVY_LIGHT}10`, fontWeight: 800 }}>
                                          <td style={{ padding: "6px 10px", color: NAVY_LIGHT, fontSize: 16,fontWeight: 700 }}>Total</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right", color: SERIES.A, fontSize: 16,fontWeight: 700 }}>{fmtAmt(jvColBreakdown.reduce((s, c) => s + c.debit, 0))}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right", color: ORANGE, fontSize: 16,fontWeight: 700 }}>{fmtAmt(jvColBreakdown.reduce((s, c) => s + c.credit, 0))}</td>
                                          <td style={{ padding: "6px 10px", textAlign: "right", color: NAVY_LIGHT, fontSize: 16,fontWeight: 700 }}>{fmtAmt(r.jvNet)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  <div style={{flex: "1 1 0",minWidth:0 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>Register Breakdown</div>
                                    <table  style={{
  width: "100%",
  tableLayout: "fixed",   // 🔥 critical
  borderCollapse: "collapse"
}}>
                                      <thead>
                                        <tr style={{ background: "#f4f7fd" }}>
                                          <th style={{ width: "45%" }}>Salary Component</th>
<th style={{ width: "30%" }}>GL Desc</th>
<th style={{ width: "25%", textAlign: "right" }}>Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {r.reg_cols.map((col, j) => (
                                          <tr key={j} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                            <td style={{ padding: "5px 10px", color: TEXT_MAIN, fontWeight: 600, fontSize: 16,fontWeight: 700 }}>{col.name}</td>
                                            <td style={{ padding: "5px 10px", color: TEXT_MUTED, fontSize: 11 }}>{col.gl_desc}</td>
                                            <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 700, color: NAVY_LIGHT, fontSize: 16,fontWeight: 700 }}>{fmtAmt(getRegVal(col))}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ background: "#f4f7fd", fontWeight: 800 }}>
                                          <td style={{ padding: "6px 10px", color: TEXT_MAIN, fontSize: 16,fontWeight: 700 }}>Total</td>
                                          <td />
                                          <td style={{ padding: "6px 10px", textAlign: "right", color: NAVY_LIGHT, fontSize: 16,fontWeight: 700 }}>{fmtAmt(r.regNet)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  <div style={{flex: "1 1 0",minWidth:0 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>Summary</div>
                                    {[
                                      { label: "GL Desc",      value: r.gl_desc,      color: TEXT_MAIN },
                                      { label: "Match By",     value: r.match_by === "desc" ? "Desc" : r.match_by === "spl" ? `SPL = ${r.spl_gl}` : `GL SAP = ${r.gl_sap}`, color: NAVY_LIGHT },
                                      { label: "JV Net",       value: fmtAmt(r.jvNet), color: SERIES.A },
                                      { label: "Register Net", value: fmtAmt(r.regNet), color: NAVY_LIGHT },
                                      { label: "Variance",     value: Math.abs(r.diff) < 1 ? "Nil" : fmtAmt(r.diff), color: Math.abs(r.diff) < 1 ? SUCCESS : DANGER },
                                    ].map((f, j) => (
                                      <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${BORDER}` }}>
                                        <span style={{ fontSize: 16,fontWeight: 700, color: TEXT_MUTED }}>{f.label}</span>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: f.color }}>{f.value}</span>
                                      </div>
                                    ))}
                                    <div style={{ marginTop: 10, padding: "9px 12px", background: r.matched ? "#f0fdf4" : "#fff0f0", border: `1px solid ${r.matched ? "#bbf7d0" : "#fecaca"}`, borderRadius: 6 }}>
                                      <div style={{ fontSize: 16,fontWeight: 700, fontWeight: 700, color: r.matched ? SUCCESS : DANGER }}>
                                        {r.matched ? "✓ Line reconciled" : "✗ Variance — review JV line items"}
                                      </div>
                                      {!r.matched && <div style={{ fontSize: 11, color: TEXT_SEC, marginTop: 3 }}>Diff of {fmtAmt(Math.abs(r.diff))} across: {(r.jv_cols || []).map(c => c.name).join(", ")}</div>}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ),
                        ];
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* Grand total */}
        <div style={{ display: "flex", alignItems: "stretch", borderLeft: `4px solid ${NAVY}`, background: "#f8fafc", borderTop: `2px solid ${BORDER}` }}>
          <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", minWidth: 220, flexShrink: 0, borderRight: `1px solid ${BORDER}` }}>
            <div>
              <div style={{ fontSize: 16,fontWeight: 700, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "1px" }}>Grand Total</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}>All salary entry lines</div>
            </div>
          </div>
          <div style={{ display: "flex", flex: 1, alignItems: "stretch" }}>
            {[
              { label: "JV Net",   value: fmtAmt(recoLines.reduce((s, r) => s + Math.abs(r.jvNet), 0)),  color: SERIES.A },
              { label: "Register", value: fmtAmt(recoLines.reduce((s, r) => s + Math.abs(r.regNet), 0)), color: NAVY_LIGHT },
              { label: "Variance", value: isFullyMatch ? "NIL" : fmtAmt(totalVariance), color: isFullyMatch ? SUCCESS : DANGER },
              { label: "Matched",  value: `${matchedLines} / ${totalLines}`, color: matchedLines === totalLines ? SUCCESS : DANGER },
            ].map((tile, ti) => (
              <div key={ti} style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1, padding: "12px 10px", borderRight: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{tile.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: tile.color }}>{tile.value}</div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 18px" }}>
              <Badge color={isFullyMatch ? SUCCESS : DANGER}>{isFullyMatch ? "✓ BALANCED" : "✗ UNBALANCED"}</Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   VALIDATION TAB
═══════════════════════════════════════════════════════════════════ */
function ValidationTab({ division, department, month }) {
  const PAGE_SIZE = 7;
  const [openCompanies, setOpenCompanies] = useState({});
  const [openJv, setOpenJv] = useState({});

  const [rows,          setRows]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [activeFilter,  setFilter]        = useState("ALL");
  const [search,        setSearch]        = useState("");
  const [expandedRow,   setExpanded]      = useState(null);
  const [page,          setPage]          = useState(1);
  const [jvTypeFilter,  setJvType]        = useState("ALL");
  const [finMode,       setFinMode]       = useState("Count");
  const FONT_SIZES = {
  title: 14,
  amount: 16,     // MAIN NUMBER
  label: 10,
  diff: 13,
};

  const fetchData = useCallback(async () => {
    if (!division?.value || !department?.value || !month) { setRows([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const whereClause = buildWhereClause({ division, department, month });
      const data = await runQuery(`SELECT * FROM jv_recon.jv ${whereClause};`);
      setRows(Array.isArray(data) ? data : data?.rows || []);
    } catch(e) { setError(e.message || "Failed to fetch data"); }
    finally    { setLoading(false); }
  }, [division, department, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const annotated  = rows.map(r => ({ ...r, _issues: validateRow(r) }));
  const violating  = annotated.filter(r => r._issues.length > 0);
  const jvTypes    = ["ALL", ...Array.from(new Set(rows.map(r => r.jv_type).filter(Boolean)))];

  const ruleBreakdown = Object.keys(ruleLabel).map(rule => ({
    rule, label: ruleLabel[rule], color: RULE_COLOR[rule],
    count: violating.filter(r => r._issues.some(i => i.rule === rule)).length,
  }));

  // Company code colours — all variants of navy/slate so they harmonise
  const COMPANY_COLORS = [NAVY, NAVY_LIGHT, "#334155", "#475569", "#0f4c7a", "#1e3a5f"];
  const COLORS = {
  debit: "#1e3a5f",      // strong navy
  credit: "#c2410c",     // DARK ORANGE (not light)
  diff_ok: "#16a34a",
  diff_bad: "#dc2626",
};

  const debitRows   = annotated.filter(r => [21,29,40].includes(Number(r.POST_KEY)));
  const creditRows  = annotated.filter(r => [31,39,50].includes(Number(r.POST_KEY)));
  const debitTotal  = debitRows.reduce((s, r) => s + (Number(r.AMOUNT) || 0), 0);
  const creditTotal = creditRows.reduce((s, r) => s + (Number(r.AMOUNT) || 0), 0);
  const netBalance  = debitTotal - creditTotal;
  const isBalanced  = Math.abs(netBalance) < 1;
function Metric({ label, value, color }) {
  return (
    <div>
      <div style={{
        fontSize: 10,
        color: "#64748b",
        fontWeight: 600,
        letterSpacing: "0.5px"
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16,
        fontWeight: 700,
        color
      }}>
        {value}
      </div>
    </div>
  );
}
  const filteredRows = violating.filter(r => {
    if (jvTypeFilter !== "ALL" && r.jv_type !== jvTypeFilter) return false;
    if (activeFilter !== "ALL" && !r._issues.some(i => i.rule === activeFilter)) return false;
    if (search) { const s = search.toLowerCase();
      return String(r.primary_id).includes(s) || String(r.gl_code || "").includes(s) ||
        String(r.ACCOUNT || "").toLowerCase().includes(s) || String(r.jv_type || "").toLowerCase().includes(s); }
    return true;
  });
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
  const pageRows   = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const FONT_PRIMARY = "Inter, Arial, sans-serif";
const FONT_NUM = "Roboto Mono, monospace";
const [openPk, setOpenPk] = useState({});

  const exportErrorRows = () => {
    if (!filteredRows.length) return;
    const exportData = filteredRows.map(r => {
      const row = {};
      ["primary_id","POST_DATE","gl_code","ACCOUNT","POST_KEY","COST_CENT","PROF_CENT",
       "AMOUNT","special_indicator","jv_type","description","account_type",
       "jv_classification","gl_desc","REFERENCE","DOC_TYPE","COMPANY_CD"].forEach(c => { row[c] = r[c] ?? ""; });
      row["exceptions"]        = r._issues?.map(i => ruleLabel[i.rule]).join("; ") || "";
      row["exception_details"] = r._issues?.map(i => i.msg).join(" | ") || "";
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = Object.keys(exportData[0] || {}).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Exception Rows");
    const label = activeFilter !== "ALL" ? `_${activeFilter}` : "";
    const monthLabel = month ? `_${month.replace("-", "")}` : "";
    XLSX.writeFile(wb, `JV_Exceptions${label}${monthLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: 60, color: TEXT_SEC, fontFamily: FONT }}>
      <div style={{ fontSize: 28, marginBottom: 8, color: ORANGE }}>◌</div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>Loading JV Register…</div>
    </div>
  );
  if (error) return (
    <div style={{ background: "#fff0f0", border: `1.5px solid ${DANGER}`, borderRadius: 8, padding: 12, color: DANGER, fontSize: 13, fontFamily: FONT }}>⚠ {error}</div>
  );

  const CARD  = { background: CARD_BG, borderRadius: 8, padding: "10px 12px", border: `1.5px solid ${BORDER}`, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" };

  const finRows = [
    { label: "Total Debit",  count: debitRows.length,  value: fmtAmt(debitTotal),  color: SERIES.A,
      pctCount: debitRows.length / Math.max(annotated.length, 1) * 100,
      pctValue: Math.abs(debitTotal) / Math.max(Math.abs(debitTotal) + Math.abs(creditTotal), 1) * 100 },
    { label: "Total Credit", count: creditRows.length, value: fmtAmt(creditTotal), color: ORANGE,
      pctCount: creditRows.length / Math.max(annotated.length, 1) * 100,
      pctValue: Math.abs(creditTotal) / Math.max(Math.abs(debitTotal) + Math.abs(creditTotal), 1) * 100 },
    { label: "Net Balance",  count: "—", value: fmtSigned(netBalance), color: isBalanced ? SUCCESS : DANGER, pctCount: 0, pctValue: 0 },
  ];

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", fontFamily: FONT }}>

      {/* ══ LEFT SIDEBAR ══ */}
      {/* ── Drilldown card: Company → JV Class → Posting Keys ── */}

<div style={{
  background: CARD_BG,
  borderRadius: 10,
  padding: "16px",
  border: `1.5px solid ${BORDER}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  width: "420px",
  flexShrink: 0,
  fontFamily: FONT_PRIMARY,
}}>

  {/* HEADER */}
  <div style={{
    fontSize: 13,
    fontWeight: 800,
    color: TEXT_SEC,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: 14,
  }}>
    Company Analysis
  </div>

  {(() => {
    const companyMap = {};

    // ───── BUILD DATA ─────
    annotated.forEach(r => {
      const c = r.COMPANY_CD || "N/A";
      const pk = String(r.POST_KEY || "NA");
      const amt = Number(r.AMOUNT || 0);
      const spl = (r.SPL_GL_IND && r.SPL_GL_IND.trim()) ? r.SPL_GL_IND : "NO IND";

      if (!companyMap[c]) {
        companyMap[c] = { debit: 0, credit: 0, jvMap: {} };
      }

      if ([40,29,21].includes(Number(pk))) companyMap[c].debit += amt;
      if ([50,39,31].includes(Number(pk))) companyMap[c].credit += amt;

      const jvArr = Array.isArray(r.jv_classification)
        ? r.jv_classification
        : [r.jv_classification ?? "UNCLASSIFIED"];

      const uniqueJV = [...new Set(jvArr.map(j => String(j || "UNCLASSIFIED").trim()))];

      uniqueJV.forEach(jv => {
        if (!companyMap[c].jvMap[jv]) {
          companyMap[c].jvMap[jv] = { debit: 0, credit: 0, pkMap: {} };
        }

        if ([40,29,21].includes(Number(pk))) companyMap[c].jvMap[jv].debit += amt;
        if ([50,39,31].includes(Number(pk))) companyMap[c].jvMap[jv].credit += amt;

        // PK LEVEL
        if (!companyMap[c].jvMap[jv].pkMap[pk]) {
          companyMap[c].jvMap[jv].pkMap[pk] = {
            total: 0,
            splMap: {}
          };
        }

        companyMap[c].jvMap[jv].pkMap[pk].total += amt;

        // SPL LEVEL
        if (!companyMap[c].jvMap[jv].pkMap[pk].splMap[spl]) {
          companyMap[c].jvMap[jv].pkMap[pk].splMap[spl] = 0;
        }

        companyMap[c].jvMap[jv].pkMap[pk].splMap[spl] += amt;
      });
    });

    // ───── SORT COMPANIES ─────
    const companies = Object.entries(companyMap)
      .map(([key, data]) => ({
        key,
        diff: data.debit - data.credit,
        jvMap: data.jvMap
      }))
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
      .slice(0, 8);

    // ───── RENDER ─────
    return companies.map(comp => {
      const isOpen = openCompanies[comp.key];
      const isBalanced = Math.abs(comp.diff) < 1;

      return (
        <div key={comp.key} style={{ marginBottom: 10 }}>

          {/* ── COMPANY ROW ── */}
          <div
            onClick={() => setOpenCompanies(p => ({ ...p, [comp.key]: !p[comp.key] }))}
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              cursor: "pointer",
              border: `1.5px solid ${isBalanced ? SUCCESS : DANGER}`,
              background: isBalanced ? "#f0fdf4" : "#fff0f0",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                {comp.key}
              </span>

              <span style={{
                fontSize: 16,
                fontWeight: 800,
                fontFamily: FONT_NUM,
                color: isBalanced ? SUCCESS : DANGER
              }}>
                {isBalanced ? 0 : fmtSigned(comp.diff)}
              </span>
            </div>
          </div>

          {/* ── JV LEVEL ── */}
          {isOpen && (
            <div style={{
              marginTop: 6,
              marginLeft: 12,
              padding: "10px",
              borderRadius: 6,
              background: "#f8fafc",
              border: `1px solid ${BORDER}`
            }}>
              {Object.entries(comp.jvMap)
                .map(([jv, data]) => ({
                  jv,
                  diff: data.debit - data.credit,
                  pkMap: data.pkMap
                }))
                .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
                .map(jvData => {
                  const jvId = `${comp.key}_${jvData.jv}`;
                  const isJvOpen = openJv[jvId];
                  const jvBalanced = Math.abs(jvData.diff) < 1;

                  return (
                    <div key={jvData.jv} style={{ marginBottom: 8 }}>

                      {/* JV ROW */}
                      <div
                        onClick={() => setOpenJv(p => ({ ...p, [jvId]: !p[jvId] }))}
                        style={{
                          padding: "10px",
                          borderRadius: 6,
                          background: "#fff",
                          border: `1px solid ${BORDER}`,
                          cursor: "pointer"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>
                            {jvData.jv}
                          </span>

                          <span style={{
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: FONT_NUM,
                            color: jvBalanced ? SUCCESS : DANGER
                          }}>
                            {jvBalanced ? "—" : fmtSigned(jvData.diff)}
                          </span>
                        </div>
                      </div>

                      {/* ── POSTING KEY LEVEL ── */}
                      {isJvOpen && (
                        <div style={{
                          marginTop: 6,
                          marginLeft: 10,
                          paddingLeft: 10,
                          borderLeft: `2px solid ${BORDER}`
                        }}>
                          {Object.entries(jvData.pkMap)
                            .sort(([,a],[,b]) => Math.abs(b.total) - Math.abs(a.total))
                            .map(([pk, pkData]) => {
                              const pkId = `${jvId}_${pk}`;
                              const isPkOpen = openPk[pkId];

                              return (
                                <div key={pk} style={{ marginBottom: 6 }}>

                                  {/* PK ROW */}
                                  <div
                                    onClick={() => setOpenPk(p => ({ ...p, [pkId]: !p[pkId] }))}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      padding: "8px 10px",
                                      borderRadius: 6,
                                      background: "#fff",
                                      border: `1px solid ${BORDER}`,
                                      cursor: "pointer"
                                    }}
                                  >
                                    <span style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: NAVY
                                    }}>
                                      PK {pk}
                                    </span>

                                    <span style={{
                                      fontSize: 13,
                                      fontWeight: 800,
                                      fontFamily: FONT_NUM
                                    }}>
                                      {fmtAmt(pkData.total)}
                                    </span>
                                  </div>

                                  {/* ── SPL LEVEL ── */}
                                  {isPkOpen && (
                                    <div style={{
                                      marginTop: 4,
                                      marginLeft: 12,
                                      borderLeft: `2px dashed ${BORDER}`,
                                      paddingLeft: 10
                                    }}>
                                      {Object.entries(pkData.splMap)
                                        .sort(([,a],[,b]) => Math.abs(b) - Math.abs(a))
                                        .map(([spl, amt]) => (
                                          <div key={spl} style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            padding: "5px 8px",
                                            marginBottom: 3,
                                            background: "#f9fafb",
                                            borderRadius: 5,
                                            fontSize: 12
                                          }}>
                                            <span style={{ color: TEXT_SEC, fontWeight: 600 }}>
                                              {spl}
                                            </span>

                                            <span style={{
                                              fontFamily: FONT_NUM,
                                              fontWeight: 700
                                            }}>
                                              {fmtAmt(amt)}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  )}

                                </div>
                              );
                            })}
                        </div>
                      )}

                    </div>
                  );
                })}
            </div>
          )}

        </div>
      );
    });
  })()}

</div>

      {/* ══ RIGHT COLUMN ══ */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>

        {/* KPI strip */}
<div style={{
  display: "grid",
  gridTemplateColumns: "1fr 1.2fr 1.8fr",
  gap: 10,
  alignItems: "stretch"
}}>

  {/* ───────────── Volume (LIGHT MODE) ───────────── */}
  <div style={{
    ...CARD,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  }}>
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: TEXT_MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      marginBottom: 10,
      fontFamily: FONT
    }}>
      Volume
    </div>

    {[
      { label: "Total",      value: fmt(rows.length),                    color: "#1e3a5f" },
      { label: "Exceptions", value: fmt(violating.length),               color: "#dc2626" },
      { label: "Clean",      value: fmt(rows.length - violating.length), color: "#16a34a" },
    ].map((item, i) => (
      <div key={i} style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: i < 2 ? `1px solid ${BORDER}` : "none",
      }}>
        <span style={{
          fontSize: 13,
          color: TEXT_SEC,
          fontWeight: 600,
          fontFamily: FONT
        }}>
          {item.label}
        </span>

        <span style={{
          fontSize: 18,
          fontWeight: 800,
          color: item.color,
          fontFamily: FONT_NUM,
          letterSpacing: "-0.3px"
        }}>
          {item.value}
        </span>
      </div>
    ))}
  </div>

  {/* ───────────── Financial ───────────── */}
  <div style={{ ...CARD }}>
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8
    }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_MUTED,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        fontFamily: FONT
      }}>
        Financial
      </div>

      <ModeToggle value={finMode} onChange={setFinMode} />
    </div>

    {finRows.map((item, i) => (
      <div key={i} style={{ marginBottom: i < 2 ? 8 : 0 }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: item.color
            }} />
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: TEXT_MAIN,
              fontFamily: FONT
            }}>
              {item.label}
            </span>
          </div>

          <span style={{
            fontSize: 14,
            fontWeight: 800,
            color: item.color,
            fontFamily: FONT_NUM
          }}>
            {finMode === "Value" ? item.value : item.count}
          </span>
        </div>

        {i < 2 && (
          <div style={{
            height: 5,
            background: "#eef2f7",
            borderRadius: 4
          }}>
            <div style={{
              height: "100%",
              borderRadius: 4,
              background: item.color,
              width: `${finMode === "Value" ? item.pctValue : item.pctCount}%`,
              transition: "width 0.35s"
            }} />
          </div>
        )}
      </div>
    ))}
  </div>

  {/* ───────────── Exceptions ───────────── */}
  <div style={{ ...CARD }}>
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: TEXT_MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      marginBottom: 8,
      fontFamily: FONT
    }}>
      Exceptions by Rule
    </div>

    <MiniDonut
      data={ruleBreakdown.map(r => ({
        value: r.count,
        color: r.color,
        label: r.label,
        rule: r.rule
      }))}
      activeFilter={activeFilter}
      onLegendClick={rule => {
        setFilter(activeFilter === rule ? "ALL" : rule);
        setPage(1);
      }}
    />
  </div>

</div>
        {/* Exception table row */}
<div style={{ display: "flex", gap: 8, flexWrap: "nowrap" }}>

          {/* Exception table */}
          <div style={{ background: CARD_BG, borderRadius: 8, border: `1.5px solid ${BORDER}`,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>

            {/* Toolbar */}
            <div style={{ padding: "8px 12px", borderBottom: `1.5px solid ${BORDER}`,
              display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", background: "#f8fafc" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: TEXT_MAIN, fontFamily: FONT }}>
                Exception Rows
                <span style={{ marginLeft: 7, fontSize: 11, fontWeight: 600, color: DANGER }}>({filteredRows.length})</span>
              </div>
              <div style={{ flex: 1 }} />
              <input value={search} placeholder="Search ID / GL / Account…"
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={{ padding: "4px 10px", border: `1.5px solid ${BORDER}`, borderRadius: 6,
                  fontSize: 16,fontWeight: 700, outline: "none", color: TEXT_MAIN, background: CARD_BG, width: 180, fontFamily: FONT }} />
              <select value={jvTypeFilter} onChange={e => { setJvType(e.target.value); setPage(1); }}
                style={{ padding: "4px 10px", border: `1.5px solid ${BORDER}`, borderRadius: 6,
                  fontSize: 16,fontWeight: 700, outline: "none", color: TEXT_MAIN, background: CARD_BG, cursor: "pointer", fontFamily: FONT }}>
                {jvTypes.map(t => <option key={t} value={t}>{t === "ALL" ? "All Types" : t}</option>)}
              </select>
              <button onClick={exportErrorRows} disabled={filteredRows.length === 0}
                style={{ background: filteredRows.length === 0 ? "#e8eef7" : DANGER, border: "none",
                  color: filteredRows.length === 0 ? TEXT_MUTED : "#fff", borderRadius: 6, padding: "5px 14px",
                  cursor: filteredRows.length === 0 ? "default" : "pointer", fontSize: 16,fontWeight: 700, fontWeight: 700,
                  fontFamily: FONT, display: "flex", alignItems: "center", gap: 4,
                  boxShadow: filteredRows.length === 0 ? "none" : `0 2px 8px ${DANGER}40` }}>
                ⬇ Export Errors
                {filteredRows.length > 0 && <span style={{ background: "#ffffff30", borderRadius: 3, padding: "1px 5px", fontSize: 10 }}>{filteredRows.length}</span>}
              </button>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                  <tr style={{ background: "#f8fafc" }}>
                    {["#ID","Post Date","GL Code","Account","Post Key","Cost Ctr","Amount","SPL Ind","JV Type","Issues"].map(h => (
                      <th key={h} style={{ padding: "9px 12px", textAlign: "left",
                        color: TEXT_MUTED, fontWeight: 700, fontSize: 11, textTransform: "uppercase",
                        letterSpacing: "0.6px", whiteSpace: "nowrap",
                        borderBottom: `1.5px solid ${BORDER}`, background: "#f8fafc" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: "48px 20px", textAlign: "center", color: TEXT_MUTED }}>
                      <div style={{ fontSize: 26, marginBottom: 6, color: SUCCESS }}>✓</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>No exceptions for current filter</div>
                    </td></tr>
                  )}
                  {pageRows.map((r, i) => {
                    const isExp = expandedRow === r.primary_id;
                    const ruleColor = r._issues[0] ? RULE_COLOR[r._issues[0].rule] : BORDER;
                    return [
                      <tr key={r.primary_id} onClick={() => setExpanded(isExp ? null : r.primary_id)}
                        style={{ background: isExp ? ORANGE_SOFT : i % 2 === 0 ? "#fff" : "#fafbfd",
                          cursor: "pointer", borderLeft: `3px solid ${ruleColor}`, transition: "background 0.1s" }}>
                        <td style={{ padding: "9px 12px", color: TEXT_MUTED, borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, fontFamily: FONT_MONO }}>{r.primary_id}</td>
                        <td style={{ padding: "9px 12px", color: TEXT_MAIN, whiteSpace: "nowrap", borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, fontFamily: FONT }}>{r.POST_DATE || "—"}</td>
                        <td style={{ padding: "9px 12px", borderBottom: `1px solid ${BORDER}` }}>
                          {r.gl_code
                            ? <span style={{ fontWeight: 700, fontSize: 16,fontWeight: 700, color: String(r.gl_code).length !== 8 ? DANGER : SUCCESS, fontFamily: FONT_MONO }}>{r.gl_code}</span>
                            : <span style={{ color: TEXT_MUTED }}>—</span>}
                        </td>
                        <td style={{ padding: "9px 12px", color: TEXT_MAIN, borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT }}>{r.ACCOUNT || "—"}</td>
                        <td style={{ padding: "9px 12px", borderBottom: `1px solid ${BORDER}` }}>{r.POST_KEY ? <PkBadge pk={r.POST_KEY} /> : <span style={{ color: TEXT_MUTED }}>—</span>}</td>
                        <td style={{ padding: "9px 12px", color: r.COST_CENT ? TEXT_MAIN : TEXT_MUTED, borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, fontFamily: FONT_MONO }}>{r.COST_CENT || "—"}</td>
                        <td style={{ padding: "9px 12px", fontWeight: 700, color: TEXT_MAIN, textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, fontFamily: FONT_MONO }}>{r.AMOUNT != null ? fmt(r.AMOUNT) : "—"}</td>
                        <td style={{ padding: "9px 12px", borderBottom: `1px solid ${BORDER}` }}>{r.special_indicator ? <Badge color={ORANGE}>{r.special_indicator}</Badge> : <span style={{ color: TEXT_MUTED }}>—</span>}</td>
                        <td style={{ padding: "9px 12px", color: TEXT_MAIN, borderBottom: `1px solid ${BORDER}`, fontSize: 16,fontWeight: 700, fontFamily: FONT }}>{r.jv_type || "—"}</td>
                        <td style={{ padding: "9px 12px", borderBottom: `1px solid ${BORDER}` }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {r._issues.map((iss, j) => <Badge key={j} color={RULE_COLOR[iss.rule] || NAVY_LIGHT}>{ruleLabel[iss.rule] || iss.rule}</Badge>)}
                          </div>
                        </td>
                      </tr>,

                      isExp && (
                        <tr key={r.primary_id + "_exp"}>
                          <td colSpan={10} style={{ background: "#f8fafc", borderBottom: `1px solid ${BORDER}`,
                            borderLeft: `3px solid ${ruleColor}`, padding: "14px 20px 16px 24px" }}>
                            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                              <div style={{ flex: 2, minWidth: 280 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 10, fontFamily: FONT }}>Full Detail</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px 20px" }}>
                                  {["REFERENCE","HEADER_TXT","DOC_TYPE","COMPANY_CD","LINE_ITEM","PLANT","PAY_KEY","CHARGE_CD","description","account_type","jv_classification","gl_desc"].map(f => (
                                    <div key={f} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <span style={{ color: TEXT_MUTED, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>{f}</span>
                                      <span style={{ color: TEXT_MAIN, fontSize: 16,fontWeight: 700, fontWeight: 600, wordBreak: "break-all", fontFamily: FONT }}>{r[f] != null ? String(r[f]) : "—"}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: DANGER, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 10, fontFamily: FONT }}>Validation Failures</div>
                                {r._issues.map((iss, j) => (
                                  <div key={j} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10,
                                    padding: "9px 12px", background: `${RULE_COLOR[iss.rule]}0d`, borderRadius: 6,
                                    border: `1px solid ${RULE_COLOR[iss.rule]}30` }}>
                                    <span style={{ color: RULE_COLOR[iss.rule], fontSize: 14, lineHeight: 1, marginTop: 1 }}>▸</span>
                                    <div>
                                      <div style={{ color: RULE_COLOR[iss.rule], fontWeight: 700, fontSize: 16,fontWeight: 700, fontFamily: FONT }}>{ruleLabel[iss.rule]}</div>
                                      <div style={{ color: TEXT_SEC, fontSize: 11, marginTop: 3, fontFamily: FONT }}>{iss.msg}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: "6px 12px", borderTop: `1.5px solid ${BORDER}`, display: "flex", gap: 5,
                justifyContent: "flex-end", alignItems: "center", background: "#fafbff" }}>
                <span style={{ fontSize: 16,fontWeight: 700, color: TEXT_MUTED, marginRight: 6, fontFamily: FONT }}>{filteredRows.length} total rows</span>
                {[{ label: "«", fn: () => setPage(1), dis: page === 1 }, { label: "‹", fn: () => setPage(p => Math.max(1, p - 1)), dis: page === 1 }]
                  .map((b, i) => <button key={i} onClick={b.fn} disabled={b.dis} style={{ background: CARD_BG, border: `1.5px solid ${BORDER}`, color: b.dis ? TEXT_MUTED : NAVY, borderRadius: 5, padding: "5px 11px", cursor: b.dis ? "default" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{b.label}</button>)}
                {[...Array(Math.min(5, totalPages))].map((_, ii) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + ii;
                  if (p < 1 || p > totalPages) return null;
                  return <button key={p} onClick={() => setPage(p)} style={{ background: p === page ? NAVY : CARD_BG, border: `1.5px solid ${p === page ? NAVY : BORDER}`, color: p === page ? "#fff" : NAVY, borderRadius: 5, padding: "5px 11px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{p}</button>;
                })}
                {[{ label: "›", fn: () => setPage(p => Math.min(totalPages, p + 1)), dis: page === totalPages }, { label: "»", fn: () => setPage(totalPages), dis: page === totalPages }]
                  .map((b, i) => <button key={i} onClick={b.fn} disabled={b.dis} style={{ background: CARD_BG, border: `1.5px solid ${BORDER}`, color: b.dis ? TEXT_MUTED : NAVY, borderRadius: 5, padding: "5px 11px", cursor: b.dis ? "default" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{b.label}</button>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const [division,          setDivision]         = useState(null);
  const [department,        setDepartment]        = useState(null);
  const [month,             setMonth]             = useState("");
  const [divisionOptions,   setDivisionOptions]   = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [activeTab,         setActiveTab]         = useState("validation");

  useEffect(() => {
    (async () => {
      try {
        const data = await runQuery(`SELECT division_id, name FROM user_management.divisions WHERE is_active = 1 and entity_id = 8;`);
        setDivisionOptions(data.map(row => ({ value: row.division_id, label: row.name })));
      } catch(e) { console.log("Failed to load divisions"); }
    })();
  }, []);

  useEffect(() => {
    if (!division) { setDepartmentOptions([]); setDepartment(null); return; }
    (async () => {
      try {
        const data = await runQuery(`SELECT department_id, name FROM user_management.department WHERE division_id = ${division.value};`);
        setDepartmentOptions(data.map(row => ({ value: row.department_id, label: row.name })));
      } catch(e) { console.log("Failed to load departments"); }
    })();
  }, [division]);

  const isReady = division && department && month;

  return (
    <div style={{ padding: 16, background: PAGE_BG, minHeight: "100vh", fontFamily: FONT }}>

      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 14px", border: `1px solid ${BORDER}`, borderRadius: 8,
        marginBottom: 12, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 10, color: TEXT_MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Month</span>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              style={{ fontSize: 13, padding: "4px 8px", height: 32, border: `1.5px solid ${BORDER_DARK}`, borderRadius: 6,
                color: TEXT_MAIN, fontFamily: FONT, outline: "none", background: "#f1f5f9", cursor: "pointer" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 10, color: TEXT_MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Division</span>
            <div style={{ minWidth: 150 }}>
              <Select placeholder="Select…" value={division} options={divisionOptions}
                onChange={selected => { setDivision(selected); setDepartment(null); }}
                styles={compactSelectStyles} />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 10, color: TEXT_MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Department</span>
            <div style={{ minWidth: 150 }}>
              <Select placeholder="Select…" value={department} options={departmentOptions}
                onChange={setDepartment} styles={compactSelectStyles} isDisabled={!division} />
            </div>
          </div>
        </div>

        {isReady && (
          <div style={{ display: "flex", background: "#e8eef7", padding: 3, borderRadius: 7, gap: 2 }}>
            {[{ key: "reco", label: "Reco" }, { key: "validation", label: "Validation" }].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: "5px 16px", fontSize: 13, fontWeight: 600, borderRadius: 5, border: "none", cursor: "pointer",
                fontFamily: FONT, transition: "all 0.15s",
                background: activeTab === tab.key ? NAVY : "transparent",
                color: activeTab === tab.key ? "#fff" : TEXT_SEC,
              }}>{tab.label}</button>
            ))}
          </div>
        )}
      </div>

      {!isReady ? (
        <div style={{ padding: 60, textAlign: "center", color: TEXT_SEC, fontWeight: 600, fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12, color: ORANGE }}>⊙</div>
          Please select Division, Department and Month to continue
        </div>
      ) : (
        <>
          {activeTab === "validation" && <ValidationTab division={division} department={department} month={month} />}
          {activeTab === "reco"       && <RecoTab       division={division} department={department} month={month} />}
        </>
      )}
    </div>
  );
}